import { Box, Typography } from '@mui/material';
import {
	PdfAnnotationSubtype,
	PdfSquareAnnoObject,
	PDFViewer,
	PluginRegistry,
} from '@embedpdf/react-pdf-viewer';
import type { DocumentManagerPlugin } from '@embedpdf/plugin-document-manager';
import type { ScrollPlugin } from '@embedpdf/plugin-scroll';
import type { SelectionPlugin } from '@embedpdf/plugin-selection';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ColorPalette } from 'shared/consts/colorPalette';
import { BackButton } from 'shared/ui/BackButton';
import { Button } from 'shared/ui/Button';
import {
	applyRegionAppearance,
	REGION_APPEARANCE,
	REGION_FILL_COLOR,
	regionStrokeColor,
} from 'components/file-editor-components/file-viewer/regionAppearance';
import {
	buildRegionFromAnnotation,
	buildSquareAnnotation,
} from 'components/file-editor-components/file-viewer/regionGeometry';
import { Region } from 'components/file-editor-components/file-viewer/region-types';
import {
	AnnotationApi,
	FileViewerHandle,
	FileViewerProps,
} from 'components/file-editor-components/file-viewer/types';
import {
	DISABLED_CATEGORIES,
	HIDDEN_UI_CATEGORIES,
} from 'components/file-editor-components/file-viewer/viewerConfig';
import { itIT } from './it-locale';
import { enUS } from './en-locale';

const FileViewer = forwardRef<FileViewerHandle, FileViewerProps>(function FileViewer(
	{
		url,
		fileName,
		regions = [],
		onRegionsChange,
		drawRegions = true,
		selectedSourceId = null,
		onSelectSource,
		onRegionNeedsLabel,
		onRegionCreated,
		onRegionUpdated,
		onDrawModeChange,
	},
	ref,
) {
	const { i18n, t } = useTranslation();
	const navigate = useNavigate();
	const [drawMode, setDrawMode] = useState(false);
	const toggleDrawMode = () => setDrawMode((prev) => !prev);

	const annotationRef = useRef<AnnotationApi | null>(null);
	const documentManagerRef = useRef<ReturnType<DocumentManagerPlugin['provides']> | null>(null);
	const scrollRef = useRef<ReturnType<ScrollPlugin['provides']> | null>(null);
	const serverRegionsInitializedRef = useRef(false);
	const previousSelectedSourceIdRef = useRef<string | null>(null);
	const lastInternalSelectionRef = useRef<string | null>(null);
	const [isDocumentOpened, setIsDocumentOpened] = useState(false);
	const [areAnnotationsLoaded, setAreAnnotationsLoaded] = useState(false);

	const regionsRef = useRef(regions);
	regionsRef.current = regions;
	const onRegionsChangeRef = useRef(onRegionsChange);
	onRegionsChangeRef.current = onRegionsChange;
	const onSelectSourceRef = useRef(onSelectSource);
	onSelectSourceRef.current = onSelectSource;
	const onRegionNeedsLabelRef = useRef(onRegionNeedsLabel);
	onRegionNeedsLabelRef.current = onRegionNeedsLabel;
	const onRegionCreatedRef = useRef(onRegionCreated);
	onRegionCreatedRef.current = onRegionCreated;
	const onRegionUpdatedRef = useRef(onRegionUpdated);
	onRegionUpdatedRef.current = onRegionUpdated;
	const onDrawModeChangeRef = useRef(onDrawModeChange);
	onDrawModeChangeRef.current = onDrawModeChange;
	const drawModeRef = useRef(drawMode);
	drawModeRef.current = drawMode;

	useEffect(() => {
		onDrawModeChangeRef.current?.(drawMode);
	}, [drawMode]);

	const suppressEmitRegionsRef = useRef(false);

	const emitRegions = useCallback(() => {
		if (suppressEmitRegionsRef.current) return;
		const annotation = annotationRef.current;
		const document = documentManagerRef.current?.getActiveDocument();
		if (!annotation || !document) return;

		const nextRegions: Region[] = [];
		annotation.getAnnotations().forEach((tracked) => {
			if (tracked.object.type !== PdfAnnotationSubtype.SQUARE) return;
			const object = tracked.object as PdfSquareAnnoObject;
			const page = document.pages[object.pageIndex];
			if (!page) return;
			nextRegions.push(buildRegionFromAnnotation(object, page));
		});
		onRegionsChangeRef.current?.(nextRegions);
	}, []);

	useImperativeHandle(
		ref,
		() => ({
			confirmPendingRegion: (region, sourceId) => {
				annotationRef.current?.updateAnnotation(region.pageIndex, region.id, {
					custom: { source: region.source, sourceId },
				});
			},
			deleteRegionBySourceId: (sourceId) => {
				const annotation = annotationRef.current;
				if (!annotation) return;
				const tracked = annotation
					.getAnnotations()
					.find((item) => item.object.custom?.sourceId === sourceId);
				if (tracked) annotation.deleteAnnotation(tracked.object.pageIndex, tracked.object.id);
			},
			resyncRegions: (nextRegions) => {
				const annotation = annotationRef.current;
				const document = documentManagerRef.current?.getActiveDocument();
				if (!annotation || !document) return;

				suppressEmitRegionsRef.current = true;
				annotation.getAnnotations().forEach((tracked) => {
					if (tracked.object.type !== PdfAnnotationSubtype.SQUARE) return;
					annotation.deleteAnnotation(tracked.object.pageIndex, tracked.object.id);
				});
				nextRegions.forEach((region) => {
					const page = document.pages[region.pageIndex];
					if (!page) return;
					annotation.createAnnotation(region.pageIndex, buildSquareAnnotation(region, page));
				});
				suppressEmitRegionsRef.current = false;
				emitRegions();
			},
		}),
		[emitRegions],
	);

	const handlePdfViewerReady = useCallback(
		(registry: PluginRegistry) => {
			const documentManager = registry.getPlugin<DocumentManagerPlugin>('document-manager')?.provides();
			const selection = registry.getPlugin<SelectionPlugin>('selection')?.provides();
			const scroll = registry.getPlugin<ScrollPlugin>('scroll')?.provides();
			const annotation = (
				registry.getPlugin('annotation') as unknown as {
					provides: () => AnnotationApi;
				} | null
			)?.provides();

			documentManagerRef.current = documentManager || null;
			scrollRef.current = scroll || null;
			annotationRef.current = annotation || null;

			annotation?.setToolDefaults('square', {
				color: REGION_FILL_COLOR,
				...REGION_APPEARANCE,
				strokeColor: regionStrokeColor('user'),
			});

			annotation?.onAnnotationEvent((event) => {
				if (event.type === 'loaded') {
					setAreAnnotationsLoaded(true);
					return;
				}
				if (event.type !== 'create' && event.type !== 'update' && event.type !== 'delete') {
					return;
				}
				if (event.annotation?.type !== PdfAnnotationSubtype.SQUARE) return;

				if (event.type === 'create' && !event.annotation.custom?.sourceId) {
					const document = documentManagerRef.current?.getActiveDocument();
					const page = document?.pages[event.annotation.pageIndex];
					if (page) {
						const region = buildRegionFromAnnotation(
							event.annotation as PdfSquareAnnoObject,
							page,
						);
						setTimeout(() => onRegionCreatedRef.current?.(region), 0);
					}
				}

				if (event.type === 'update' && event.annotation.custom?.sourceId) {
					const document = documentManagerRef.current?.getActiveDocument();
					const page = document?.pages[event.annotation.pageIndex];
					if (page) {
						const region = buildRegionFromAnnotation(
							event.annotation as PdfSquareAnnoObject,
							page,
						);
						onRegionUpdatedRef.current?.(region);
					}
				}

				emitRegions();
			});

			annotation?.onStateChange((event) => {
				const selectedUid = event.state.selectedUids[0];
				if (!selectedUid) {
					lastInternalSelectionRef.current = null;
					onSelectSourceRef.current?.(null);
					return;
				}
				const tracked = annotation.getAnnotationById(selectedUid);
				const sourceId = tracked?.object.custom?.sourceId as string | undefined;
				lastInternalSelectionRef.current = sourceId ?? null;
				onSelectSourceRef.current?.(sourceId ?? null);

				if (
					drawModeRef.current &&
					event.state.selectedUids.length === 1 &&
					tracked?.object.type === PdfAnnotationSubtype.SQUARE &&
					!sourceId
				) {
					const object = tracked.object as PdfSquareAnnoObject;
					const document = documentManagerRef.current?.getActiveDocument();
					const page = document?.pages[object.pageIndex];
					if (page) {
						onRegionNeedsLabelRef.current?.(buildRegionFromAnnotation(object, page));
					}
				}
			});

			documentManager?.onDocumentOpened(() => {
				selection?.enableForMode('pointerMode', {
					enableSelection: false,
					showSelectionRects: false,
					enableMarquee: false,
					showMarqueeRects: false,
				});
				setIsDocumentOpened(true);
			});
		},
		[emitRegions],
	);

	useEffect(() => {
		if (!areAnnotationsLoaded || serverRegionsInitializedRef.current) return;
		const annotation = annotationRef.current;
		const document = documentManagerRef.current?.getActiveDocument();
		if (!annotation || !document || regionsRef.current.length === 0) return;

		serverRegionsInitializedRef.current = true;
		suppressEmitRegionsRef.current = true;
		regionsRef.current.forEach((region) => {
			const page = document.pages[region.pageIndex];
			if (!page) return;
			annotation.createAnnotation(region.pageIndex, buildSquareAnnotation(region, page));
		});
		suppressEmitRegionsRef.current = false;
		emitRegions();
	}, [areAnnotationsLoaded, regions, emitRegions]);

	useEffect(() => {
		if (!isDocumentOpened) return;
		const annotation = annotationRef.current;
		if (!annotation) return;

		annotation.setActiveTool(drawMode ? 'square' : null);

		const squareTool = annotation.getTool('square');
		if (squareTool) {
			annotation.addTool({
				...squareTool,
				interaction: {
					...squareTool.interaction,
					isRotatable: false,
					isGroupRotatable: false,
					isDraggable: drawMode,
					isResizable: drawMode,
					isGroupDraggable: drawMode,
					isGroupResizable: drawMode,
				},
				behavior: {
					...squareTool.behavior,
					selectAfterCreate: false,
				},
			});
		}
	}, [drawMode, isDocumentOpened]);

	useEffect(() => {
		if (!drawMode) return;
		return () => {
			const annotation = annotationRef.current;
			if (!annotation) return;
			annotation.getAnnotations().forEach((tracked) => {
				if (tracked.object.type !== PdfAnnotationSubtype.SQUARE) return;
				const object = tracked.object as PdfSquareAnnoObject;
				if (!object.custom?.sourceId) {
					annotation.deleteAnnotation(object.pageIndex, object.id);
				}
			});
		};
	}, [drawMode]);

	useEffect(() => {
		if (!isDocumentOpened) return;
		const annotation = annotationRef.current;
		if (!annotation) return;

		const previousSourceId = previousSelectedSourceIdRef.current;
		if (previousSourceId && previousSourceId !== selectedSourceId) {
			applyRegionAppearance(annotation, previousSourceId, false);
		}
		previousSelectedSourceIdRef.current = selectedSourceId;

		if (!selectedSourceId) {
			annotation.deselectAnnotation();
			return;
		}

		const cameFromOutsideFile = selectedSourceId !== lastInternalSelectionRef.current;

		const matching = annotation
			.getAnnotations()
			.filter((tracked) => tracked.object.custom?.sourceId === selectedSourceId);
		if (matching.length > 0) {
			annotation.setSelection(matching.map((tracked) => tracked.object.id));
			applyRegionAppearance(annotation, selectedSourceId, true);
		}

		if (cameFromOutsideFile) {
			const tracked = matching[0];
			const scroll = scrollRef.current;
			if (tracked && scroll) {
				scroll.scrollToPage({
					pageNumber: tracked.object.pageIndex + 1,
					pageCoordinates: { x: tracked.object.rect.origin.x, y: tracked.object.rect.origin.y },
					behavior: 'smooth',
					alignX: 50,
					alignY: 50,
				});
			}
		}
	}, [selectedSourceId, isDocumentOpened]);

	return (
		<Box
			sx={{
				flex: 1,
				minHeight: 0,
				alignSelf: 'stretch',
				display: 'flex',
				flexDirection: 'column',
				gap: '8px',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					flexShrink: 0,
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
					<BackButton action={() => navigate(-1)} sx={{ position: 'static' }} />
					<Typography variant={'h6'} color={ColorPalette.astronaut}>
						{fileName}
					</Typography>
				</Box>
				{drawRegions && (
					<Button
						size={'small'}
						variant={drawMode ? 'contained' : 'outlined'}
						onClick={toggleDrawMode}
					>
						{drawMode ? t('shared_texts.cancel') : t('file_editor.draw_region')}
					</Button>
				)}
			</Box>
			<Box
				sx={{
					'flex': 1,
					'minHeight': 0,
					'& > div': { height: '100%' },
				}}
			>
				<PDFViewer
					onReady={handlePdfViewerReady}
					config={{
						src: url,
						theme: { preference: 'light' },
						i18n: {
							defaultLocale: i18n.language,
							locales: [itIT, enUS],
						},
						selection: {
							marquee: { enabled: false },
						},
						disabledCategories: DISABLED_CATEGORIES,
						ui: {
							disabledCategories: HIDDEN_UI_CATEGORIES,
						},
					}}
				/>
			</Box>
		</Box>
	);
});

export default FileViewer;
